'use client';

import React, { useState } from 'react';
import { CheckCircle, AlertTriangle, ChevronDown, Printer, Share2, Lock } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import GradeBadge from '@/components/ui/GradeBadge';
import Modal from '@/components/ui/Modal';
import LotClosureConfirm from './LotClosureConfirm';
import SettlementSummaryChart from './SettlementSummaryChart';

const LOTS_FOR_SETTLEMENT = [
  { value: 'lot-211', label: 'Lot #211 — Tomato — Suresh Farm — Fully Sold', status: 'pending' as const },
  { value: 'lot-185', label: 'Lot #185 — Onion — Kumar Brothers — Fully Sold', status: 'pending' as const },
  { value: 'lot-300', label: 'Lot #300 — Onion — Rajesh Traders — Selling', status: 'active' as const },
  { value: 'lot-092', label: 'Lot #092 — Potato — Srinivas Agro — Settled', status: 'settled' as const },
];

const SETTLEMENT_DATA: Record<string, {
  lotNumber: string;
  product: string;
  distributor: string;
  arrivalDate: string;
  totalBags: number;
  totalWeight: number;
  soldBags: number;
  soldWeight: number;
  gradeBreakdown: { grade: string; bags: number; weight: number; rate: number; amount: number }[];
  expenses: { type: string; amount: number }[];
  paymentsReceived: { party: string; method: string; amount: number; date: string }[];
  paymentsSent: { party: string; method: string; amount: number; date: string }[];
  commission: number;
  commissionRate: number;
  status: 'pending' | 'active' | 'settled';
}> = {
  'lot-211': {
    lotNumber: '211',
    product: 'Tomato',
    distributor: 'Suresh Farm',
    arrivalDate: '14/06/2026',
    totalBags: 100,
    totalWeight: 5000,
    soldBags: 100,
    soldWeight: 5000,
    gradeBreakdown: [
      { grade: 'A', bags: 35, weight: 1750, rate: 28.0, amount: 49000 },
      { grade: 'B', bags: 42, weight: 2100, rate: 22.0, amount: 46200 },
      { grade: 'C', bags: 23, weight: 1150, rate: 15.0, amount: 17250 },
    ],
    expenses: [
      { type: 'Hamali (Loading/Unloading)', amount: 3500 },
      { type: 'Market Fee (Tolai)', amount: 2800 },
      { type: 'Transport / Lorry', amount: 2000 },
      { type: 'Labour Charges', amount: 1500 },
    ],
    paymentsReceived: [
      { party: 'Rajan Supermarket', method: 'UPI', amount: 70000, date: '16/06/2026' },
      { party: 'Vignesh Wholesale', method: 'Bank Transfer', amount: 46200, date: '17/06/2026' },
      { party: 'Anbu Traders', method: 'Cash', amount: 17250, date: '15/06/2026' },
    ],
    paymentsSent: [
      { party: 'Suresh Farm', method: 'Bank Transfer', amount: 95000, date: '17/06/2026' },
    ],
    commission: 11250,
    commissionRate: 6.43,
    status: 'pending',
  },
  'lot-185': {
    lotNumber: '185',
    product: 'Onion',
    distributor: 'Kumar Brothers',
    arrivalDate: '12/06/2026',
    totalBags: 180,
    totalWeight: 9000,
    soldBags: 180,
    soldWeight: 9000,
    gradeBreakdown: [
      { grade: 'A', bags: 68, weight: 3400, rate: 19.0, amount: 64600 },
      { grade: 'B', bags: 82, weight: 4100, rate: 14.5, amount: 59450 },
      { grade: 'C', bags: 30, weight: 1500, rate: 9.5, amount: 14250 },
    ],
    expenses: [
      { type: 'Hamali (Loading/Unloading)', amount: 6300 },
      { type: 'Market Fee (Tolai)', amount: 4700 },
      { type: 'Transport / Lorry', amount: 4800 },
      { type: 'Labour Charges', amount: 2400 },
    ],
    paymentsReceived: [
      { party: 'Selvam & Co', method: 'UPI', amount: 74050, date: '14/06/2026' },
      { party: 'Murugan Wholesale', method: 'UPI', amount: 64250, date: '15/06/2026' },
      { party: 'Various Buyers', method: 'Cash', amount: 100000, date: '16/06/2026' },
    ],
    paymentsSent: [
      { party: 'Kumar Brothers', method: 'Bank Transfer', amount: 180000, date: '17/06/2026' },
    ],
    commission: 19600,
    commissionRate: 14.18,
    status: 'pending',
  },
};

export default function SettlementClient() {
  const [selectedLot, setSelectedLot] = useState('lot-211');
  const [showClosureModal, setShowClosureModal] = useState(false);

  const data = SETTLEMENT_DATA[selectedLot];

  const handleWhatsAppShare = () => {
    if (!data) return;
    const totalSalesValue = data.gradeBreakdown.reduce((s, g) => s + g.amount, 0);
    const totalExpenses = data.expenses.reduce((s, e) => s + e.amount, 0);
    const totalReceived = data.paymentsReceived.reduce((s, p) => s + p.amount, 0);
    const totalSent = data.paymentsSent.reduce((s, p) => s + p.amount, 0);
    const netSettlement = totalReceived - totalSent - totalExpenses;

    const message =
      `*Settlement Report — Lot #${data.lotNumber}*\n` +
      `Product: ${data.product} | Distributor: ${data.distributor}\n` +
      `Arrival: ${data.arrivalDate}\n\n` +
      `*Sales Summary*\n` +
      `Total Sales Value: ₹${totalSalesValue.toLocaleString('en-IN')}\n` +
      `Total Expenses: ₹${totalExpenses.toLocaleString('en-IN')}\n` +
      `Commission Earned: ₹${data.commission.toLocaleString('en-IN')}\n\n` +
      `*Balance to Pay / Receive: ${netSettlement >= 0 ? '+' : ''}₹${Math.abs(netSettlement).toLocaleString('en-IN')}*\n\n` +
      `Status: ${data.status.toUpperCase()}`;

    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const totalSalesValue = data?.gradeBreakdown.reduce((s, g) => s + g.amount, 0) || 0;
  const totalExpenses = data?.expenses.reduce((s, e) => s + e.amount, 0) || 0;
  const totalReceived = data?.paymentsReceived.reduce((s, p) => s + p.amount, 0) || 0;
  const totalSent = data?.paymentsSent.reduce((s, p) => s + p.amount, 0) || 0;
  const netSettlement = totalSalesValue - totalExpenses - data?.commission - totalSent;
  const pendingFromBuyers = totalSalesValue - totalReceived;

  return (
    <div className="space-y-4">
      {/* Lot Selector */}
      <div className="card-base p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1 w-full">
            <label className="block text-xs font-semibold text-foreground mb-1.5">Select Lot for Settlement</label>
            <div className="relative">
              <select
                value={selectedLot}
                onChange={(e) => setSelectedLot(e.target.value)}
                className="input-field appearance-none pr-10"
              >
                {LOTS_FOR_SETTLEMENT.map((l) => (
                  <option key={`settle-lot-${l.value}`} value={l.value}>{l.label}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
          </div>
          {data && (
            <div className="flex items-center gap-2">
              <StatusBadge status={data.status} />
              <button className="btn-ghost p-2">
                <Printer size={16} />
              </button>
              <button className="btn-ghost p-2" onClick={handleWhatsAppShare}>
                <Share2 size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {data && (
        <>
          {/* Settlement Header Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3">
            {[
              { label: 'Total Sales Value', value: `₹${totalSalesValue.toLocaleString('en-IN')}`, color: 'text-accent', bg: 'bg-accent/5 border-accent/20' },
              { label: 'Total Expenses', value: `₹${totalExpenses.toLocaleString('en-IN')}`, color: 'text-danger', bg: 'bg-danger/5 border-danger/20' },
              { label: 'Commission Earned', value: `₹${data.commission.toLocaleString('en-IN')}`, color: 'text-primary', bg: 'bg-primary/5 border-primary/20' },
              { label: 'Net Settlement', value: `₹${Math.abs(netSettlement).toLocaleString('en-IN')}`, color: netSettlement >= 0 ? 'text-accent' : 'text-warning', bg: netSettlement >= 0 ? 'bg-accent/5 border-accent/20' : 'bg-warning/5 border-warning/20' },
            ].map((s) => (
              <div key={`settle-kpi-${s.label}`} className={`card-base p-4 border ${s.bg}`}>
                <p className="metric-label">{s.label}</p>
                <p className={`text-xl font-bold amount-display mt-1 ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Chart + Grade Breakdown side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SettlementSummaryChart
              salesValue={totalSalesValue}
              expenses={totalExpenses}
              commission={data.commission}
              sent={totalSent}
            />

            {/* Grade Breakdown */}
            <div className="card-base p-4">
              <h3 className="text-sm font-semibold text-foreground mb-4">Grade-wise Sales Breakdown</h3>
              <div className="space-y-3">
                {data.gradeBreakdown.map((g) => (
                  <div key={`grade-row-${g.grade}`} className="bg-muted/30 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2">
                      <GradeBadge grade={g.grade as 'A' | 'B' | 'C'} />
                      <span className="text-sm font-bold amount-display text-foreground">₹{g.amount.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <p className="text-muted-foreground">Bags</p>
                        <p className="font-semibold">{g.bags}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Weight</p>
                        <p className="font-semibold amount-display">{g.weight.toLocaleString('en-IN')} kg</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Rate/kg</p>
                        <p className="font-semibold amount-display">₹{g.rate.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Expenses */}
          <div className="card-base p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Expenses Deducted</h3>
            <div className="space-y-2">
              {data.expenses.map((exp) => (
                <div key={`exp-settle-${exp.type}`} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                  <span className="text-sm text-muted-foreground">{exp.type}</span>
                  <span className="text-sm font-semibold text-danger amount-display">-₹{exp.amount.toLocaleString('en-IN')}</span>
                </div>
              ))}
              <div className="flex items-center justify-between py-2 border-t border-border">
                <span className="text-sm font-semibold text-foreground">Total Expenses</span>
                <span className="text-sm font-bold text-danger amount-display">-₹{totalExpenses.toLocaleString('en-IN')}</span>
              </div>
            </div>
          </div>

          {/* Payments Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Received from Buyers */}
            <div className="card-base p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">Payments Received (from Buyers)</h3>
              <div className="space-y-2">
                {data.paymentsReceived.map((p, idx) => (
                  <div key={`recv-${idx}`} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <div>
                      <p className="text-xs font-medium text-foreground">{p.party}</p>
                      <p className="text-2xs text-muted-foreground">{p.method} · {p.date}</p>
                    </div>
                    <span className="text-sm font-bold text-accent amount-display">+₹{p.amount.toLocaleString('en-IN')}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between py-2 border-t border-border">
                  <span className="text-sm font-semibold">Total Received</span>
                  <span className="text-sm font-bold text-accent amount-display">₹{totalReceived.toLocaleString('en-IN')}</span>
                </div>
                {pendingFromBuyers > 0 && (
                  <div className="flex items-center gap-2 bg-warning/5 border border-warning/20 rounded-lg p-2">
                    <AlertTriangle size={12} className="text-warning" />
                    <span className="text-xs text-warning font-medium">
                      ₹{pendingFromBuyers.toLocaleString('en-IN')} still pending from buyers
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Sent to Distributor */}
            <div className="card-base p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">Payments Sent (to Distributor)</h3>
              <div className="space-y-2">
                {data.paymentsSent.map((p, idx) => (
                  <div key={`sent-${idx}`} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <div>
                      <p className="text-xs font-medium text-foreground">{p.party}</p>
                      <p className="text-2xs text-muted-foreground">{p.method} · {p.date}</p>
                    </div>
                    <span className="text-sm font-bold text-info amount-display">-₹{p.amount.toLocaleString('en-IN')}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between py-2 border-t border-border">
                  <span className="text-sm font-semibold">Total Sent</span>
                  <span className="text-sm font-bold text-info amount-display">₹{totalSent.toLocaleString('en-IN')}</span>
                </div>
              </div>

              {/* Commission */}
              <div className="mt-3 bg-primary/5 border border-primary/20 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-primary">Commission Earned</p>
                    <p className="text-2xs text-muted-foreground">{data.commissionRate.toFixed(2)}% of sales</p>
                  </div>
                  <span className="text-lg font-bold text-primary amount-display">₹{data.commission.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Final Settlement Summary */}
          <div className="card-base p-5 border-2 border-primary/20">
            <h3 className="text-sm font-semibold text-foreground mb-4">Final Settlement Summary</h3>
            <div className="space-y-2">
              {[
                { label: 'Total Sales Value', value: `₹${totalSalesValue.toLocaleString('en-IN')}`, color: 'text-foreground' },
                { label: '− Total Expenses', value: `-₹${totalExpenses.toLocaleString('en-IN')}`, color: 'text-danger' },
                { label: '− Commission (Dealer)', value: `-₹${data.commission.toLocaleString('en-IN')}`, color: 'text-primary' },
                { label: '= Distributor Net Amount', value: `₹${(totalSalesValue - totalExpenses - data.commission).toLocaleString('en-IN')}`, color: 'text-foreground' },
                { label: '− Already Sent to Distributor', value: `-₹${totalSent.toLocaleString('en-IN')}`, color: 'text-info' },
              ].map((row) => (
                <div key={`final-${row.label}`} className="flex items-center justify-between py-1.5 border-b border-border/40 last:border-0">
                  <span className="text-sm text-muted-foreground">{row.label}</span>
                  <span className={`text-sm font-semibold amount-display ${row.color}`}>{row.value}</span>
                </div>
              ))}
              <div className="flex items-center justify-between py-3 bg-muted/30 rounded-xl px-3 mt-2">
                <span className="text-sm font-bold text-foreground">Balance to Pay / Receive</span>
                <span className={`text-xl font-bold amount-display ${netSettlement >= 0 ? 'text-accent' : 'text-warning'}`}>
                  {netSettlement >= 0 ? '+' : ''}₹{Math.abs(netSettlement).toLocaleString('en-IN')}
                </span>
              </div>
            </div>

            {/* Closure Action */}
            <div className="flex gap-3 mt-5">
              <button className="btn-secondary flex-1" onClick={handleWhatsAppShare}>
                <Share2 size={14} />
                WhatsApp Report
              </button>
              <button className="btn-secondary flex-1">
                <Printer size={14} />
                Print Settlement
              </button>
              {data.status !== 'settled' && (
                <button
                  onClick={() => setShowClosureModal(true)}
                  className="btn-primary flex-1"
                >
                  <Lock size={14} />
                  Close Lot
                </button>
              )}
              {data.status === 'settled' && (
                <div className="flex-1 flex items-center justify-center gap-2 bg-accent/10 border border-accent/20 rounded-xl text-accent font-semibold text-sm">
                  <CheckCircle size={14} />
                  Lot Closed
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Closure Confirm Modal */}
      <Modal
        isOpen={showClosureModal}
        onClose={() => setShowClosureModal(false)}
        title="Close Lot — Final Confirmation"
        size="md"
      >
        <LotClosureConfirm
          lotNumber={data?.lotNumber || ''}
          pendingAmount={pendingFromBuyers}
          onClose={() => setShowClosureModal(false)}
        />
      </Modal>
    </div>
  );
}